import { useState, useEffect, useContext } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

// Tema estándar de SIACDEV1.0
const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarCentrosPPE = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Guardamos el código original para enviarlo al backend como centcodigoOld
  const [centcodigoViejo, setCentcodigoViejo] = useState("")

  // Estado del formulario
  const [formData, setFormData] = useState({
    centcodigo: "",
    centdescri: "",
    centstatus: "A",
  })

  // Cargar datos al montar el componente
  useEffect(() => {
    if (location.state) {
      setFormData({
        centcodigo: location.state.centcodigo || "",
        centdescri: location.state.centdescri || "",
        centstatus: location.state.centstatus || "A",
      })
      setCentcodigoViejo(location.state.centcodigo || "")
    } else {
      // Si entran directamente a la URL sin pasar por la grilla, los devolvemos
      navigate(-1)
    }
  }, [location, navigate])

  // Mutación para actualizar en el backend
  const { mutateAsync: SaveEdicionCentro, isPending: isSavingEdicion } = useMutation({
    queryKey: ["isUpdatingCentro"],
    fn: async (data) => {
      const response = await api.post("/CentrosPPE/updateCentrosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejador de cambios con limpieza (strip/upper) y límites
  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    if (field === "centcodigo") {
      finalValue = finalValue.slice(0, 4)
    } else if (field === "centdescri") {
      finalValue = finalValue.slice(0, 100)
    }

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejador de guardado
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones del Frontend
    if (!formData.centcodigo.trim()) {
      showWarning("El código del centro es obligatorio")
      return
    }
    if (!formData.centdescri.trim()) {
      showWarning("La descripción es obligatoria")
      return
    }

    // Armamos el Payload exactamente como lo espera nuestro Flask backend
    const payload = {
      centcodigoOld: centcodigoViejo,
      centcodigoNew: formData.centcodigo,
      centdescri: formData.centdescri,
      centstatus: formData.centstatus,
    }

    try {
      await SaveEdicionCentro(payload)
    } catch (error) {
      console.error("Error al editar el centro:", error)
    }
  }

  // Permisos dinámicos para el botón
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (a) =>
      a?.acccaption === "ACTUALIZAR" ||
      a?.acccaption === "GUARDAR" ||
      a?.acccaption === "EDITAR" ||
      a?.acccaption === "EJECUTAR",
  )

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Centro de Activo: {centcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, mb: 3, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Centro
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.centcodigo}
                  onChange={(e) => handleInputChange("centcodigo", e.target.value)}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.centdescri}
                  onChange={(e) => handleInputChange("centdescri", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.centstatus}
                  onChange={(e) => handleInputChange("centstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarCentrosPPE
